import React from "react";

import { Grid, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AiFillStar } from "react-icons/ai";
import { useHistory } from "react-router-dom";

import { PRIVATE_ROUTES } from "../../../../../constants";
import { useAppState } from "../../../../../context/StateContext";
import { Checklist } from "../../../../../models/checklist";
import { Report } from "../../../../../models/report";
import { ReportGroup } from "../../../../../models/report.group";
import { ReportGroup as EReportGroup, ReportName } from "../../../../../util/enums";
import env from "../../../../../util/env";
import { includeInSearch } from "../../../../../util/includeInSearch";
import useStyles from "./styles";

interface FavoriteReportsProps {
  reportsGroup: ReportGroup[];
  removeFromFavorites: (reportId?: number, checklistId?: number) => void;
  searchValue?: string;
  favoriteReports: Report[];
  favoriteChecklists: Checklist[];
}

const FavoriteReports = ({
  reportsGroup,
  removeFromFavorites,
  searchValue,
  favoriteReports,
  favoriteChecklists,
}: FavoriteReportsProps) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();
  const history = useHistory();
  const accessEmails = ["peter.smith@cottonon.com.au", "adrian.ficorilli@shipeezi.com"];

  const getDefaultReportGroup = () => {
    if (reportsGroup && reportsGroup?.length) {
      return reportsGroup?.find((reportGroup) => reportGroup.group === EReportGroup.Operations);
    }

    return undefined;
  };

  const renderReport = (
    name: string,
    reportId?: number,
    checklistId?: number,
    reportGroup?: ReportGroup
  ) => {
    if (reportGroup && (reportId || checklistId)) {
      return (
        <Grid
          key={checklistId || reportId}
          data-cy={`reportsMainScreenFavoriteItem${checklistId || reportId}`}
          item
          xs={12}
          sm={4}
          className={classes.reportFavoriteWrapper}
        >
          <Grid container className={classes.reportFavoriteContainer}>
            <IconButton
              className={classes.favoriteIcon}
              onClick={() => removeFromFavorites(reportId, checklistId)}
              data-cy={`reportsMainScreenFavoritesRemove${checklistId || reportId}`}
            >
              <AiFillStar color="#FEC84B" size={16} />
            </IconButton>
            <span
              onClick={() =>
                history.push(`${PRIVATE_ROUTES.ANALYTICS_MNT}`, {
                  reportGroup,
                  name,
                  checklistId,
                  reportId,
                })
              }
              className={classes.reportFavorite}
            >
              {name}
            </span>
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const renderReports = () => {
    if (favoriteReports && favoriteReports?.length) {
      return favoriteReports?.map((favoriteReport) => {
        if (
          favoriteReport?.name === ReportName.SHIPMENT_COST_REPORT &&
          !accessEmails.includes(currentUser?.username || "") &&
          env.NODE_ENV === "production"
        )
          return null;
        if (favoriteReport?.id && favoriteReport?.name) {
          return renderReport(
            favoriteReport?.name,
            favoriteReport?.id,
            undefined,
            favoriteReport?.reportGroup
          );
        }

        return null;
      });
    }

    return null;
  };

  const renderChecklists = () => {
    if (favoriteChecklists && favoriteChecklists?.length) {
      return favoriteChecklists?.map((favoriteChecklist) => {
        if (
          favoriteChecklist?.id &&
          favoriteChecklist?.description &&
          includeInSearch(favoriteChecklist?.description, searchValue)
        ) {
          return renderReport(
            favoriteChecklist?.description,
            undefined,
            favoriteChecklist?.id,
            getDefaultReportGroup()
          );
        }

        return null;
      });
    }

    return null;
  };

  const renderContent = () => {
    const checklistsFiltered =
      favoriteChecklists && favoriteChecklists?.length && searchValue
        ? favoriteChecklists?.filter((checklist) =>
            includeInSearch(checklist?.description, searchValue)
          )
        : favoriteChecklists || [];

    const reportsFiltered =
      favoriteReports && favoriteReports?.length && searchValue
        ? favoriteReports?.filter((report) => includeInSearch(report?.name, searchValue))
        : favoriteReports || [];

    if (currentUser && (checklistsFiltered?.length || reportsFiltered?.length)) {
      return (
        <>
          {renderReports()}
          {renderChecklists()}
        </>
      );
    }

    return (
      <span data-cy="reportsMainScreenFavoritesEmpty" className={classes.favoritesEmpty}>
        {translate("noReportsAddedToFavorites")}
      </span>
    );
  };

  return (
    <Grid container>
      <span data-cy="reportsMainScreenFavoritesTitle" className={classes.reportSubheader}>
        {translate("favorites")}
      </span>

      <Grid
        data-cy="reportsMainScreenFavoritesContainer"
        container
        spacing={2}
        className={classes.reportFavoriteItems}
      >
        {renderContent()}
      </Grid>
    </Grid>
  );
};

export default FavoriteReports;
